import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store";
import { fetchMyTickets, FetchMyTicketsParams } from "../../../store/thunks/ticketingPortalThunk";
import { MyTicketRow, TicketStatus, addUserTicketNotif, UserTicketNotif, saveMyTicketsListState, setMyTickets, setMyTicketsTotalElements } from "../../../store/slices/ticketingPortalSlice";

export type MyTicketStatusPill = {
  key: "all" | TicketStatus;
  label: string;
  dotColor?: string;
};

export type SortColumn =
  | "id"
  | "title"
  | "category"
  | "subcategory"
  | "location"
  | "urgency"
  | "status"
  | "submitted"
  | "lastUpdated";

export type SortDirection = "asc" | "desc";

export type WebFilters = {
  urgency: string[];
  status: string[];
  category: string[];
  subcategory: string[];
  location: string[];
  title: string[];
};

export type FilterOptions = {
  urgencies: string[];
  statuses: string[];
  categories: string[];
  subcategories: string[];
  locations: string[];
  titles: string[];
};

/** Internal status key → API status string */
const STATUS_TO_API: Record<string, string> = {
  new: "New",
  progress: "In Progress",
  "waiting-it": "Waiting on IT",
  "waiting-tmv": "Waiting on Vendor",
  "waiting-vendor": "Waiting on Vendor",
  "submitted-ado": "Submitted to ADO",
  "waiting-user": "Waiting on User",
  resolved: "Closed",
};

/** Internal urgency key → API urgency string */
const URGENCY_TO_API: Record<string, string> = {
  critical: "Critical",
  high: "High",
  medium: "Medium",
  low: "Low",
};

/** SortColumn → API sortBy field name */
const SORT_COLUMN_TO_API: Record<string, string> = {
  id: "ticketNumber",
  title: "title",
  urgency: "urgency",
  status: "status",
  submitted: "createdAt",
  lastUpdated: "createdAt",
  location: "levelReferenceId",
  category: "createdAt",
  subcategory: "createdAt",
};

// User email is resolved from appData first, then auth fallback.

export const EMPTY_WEB_FILTERS: WebFilters = {
  urgency: [],
  status: [],
  category: [],
  subcategory: [],
  location: [],
  title: [],
};

const STATUS_PILLS: MyTicketStatusPill[] = [
  { key: "all", label: "All" },
  { key: "new", label: "New", dotColor: "#005CBC" },
  { key: "progress", label: "In Progress", dotColor: "#7871E2" },
  { key: "waiting-it", label: "Escalated to IT", dotColor: "#FCC42B" },
  { key: "waiting-vendor", label: "Waiting on Vendor", dotColor: "#0078A7" },
  { key: "submitted-ado", label: "Submitted to ADO", dotColor: "#5C4EC9" },
  { key: "waiting-user", label: "Waiting on User", dotColor: "#FF8C09" },
  { key: "resolved", label: "Closed", dotColor: "#027C06" },
];

const URGENCY_SORT_ORDER: Record<string, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
  informational: 4,
};

const STATUS_SORT_ORDER: Record<string, number> = {
  new: 0,
  progress: 1,
  "waiting-it": 2,
  "waiting-user": 3,
  "waiting-tmv": 4,
  "waiting-vendor": 4,
  "submitted-ado": 5,
  resolved: 6,
};

function sortTickets(
  tickets: MyTicketRow[],
  column: SortColumn,
  direction: SortDirection
): MyTicketRow[] {
  const multiplier = direction === "asc" ? 1 : -1;
  return [...tickets].sort((a, b) => {
    let cmp = 0;
    switch (column) {
      case "id":
        cmp = a.id.localeCompare(b.id);
        break;
      case "title":
        cmp = a.title.localeCompare(b.title);
        break;
      case "category":
        cmp = a.category.localeCompare(b.category);
        break;
      case "subcategory":
        cmp = (a.subcategory ?? "").localeCompare(b.subcategory ?? "");
        break;
      case "location":
        cmp = a.location.localeCompare(b.location);
        break;
      case "urgency":
        cmp = (URGENCY_SORT_ORDER[a.urgency] ?? 99) - (URGENCY_SORT_ORDER[b.urgency] ?? 99);
        break;
      case "status":
        cmp = (STATUS_SORT_ORDER[a.status] ?? 99) - (STATUS_SORT_ORDER[b.status] ?? 99);
        break;
      case "submitted":
        cmp = new Date(a.submitted).getTime() - new Date(b.submitted).getTime();
        break;
      case "lastUpdated":
        cmp = new Date(a.lastUpdated).getTime() - new Date(b.lastUpdated).getTime();
        break;
    }
    return cmp * multiplier;
  });
}

const useMyTicketsListing = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { myTickets, notifiedWaitingTicketIds, myTicketsLoading, myTicketsError, myTicketsListState } = useSelector((state: RootState) => state.ticketingPortal);
  const categoryConfig = useSelector((state: RootState) => state.ticketingPortal.submitTicketCategoryConfig);
  const userEmail = useSelector((state: RootState) => {
    const authUser = state.auth?.user as any;
    return String(
      state.appData?.userInfo?.email
        ?? authUser?.email
        ?? authUser?.userPrincipalName
        ?? authUser?.username
        ?? ""
    ).trim();
  });
  const noAccessMode = useSelector((state: RootState) => state.appData?.noAccessMode);
  const reduxTotalElements = useSelector((state: RootState) => state.ticketingPortal.myTicketsTotalElements);
  const reduxClosedCount = useSelector((state: RootState) => state.ticketingPortal.closedTicketsCount);

  // ── Restore list state when returning from Ticket Detail ─────────────────
  const restoredState = myTicketsListState;

  const [query, setQuery] = useState(() => restoredState?.query ?? "");
  const [activeStatus, setActiveStatus] = useState<"all" | TicketStatus>(
    () => restoredState?.activeStatus ?? "all"
  );
  const [sortColumn, setSortColumn] = useState<SortColumn>(
    () => (restoredState?.sortColumn as SortColumn) ?? "lastUpdated"
  );
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    () => restoredState?.sortDirection ?? "desc"
  );
  const [webFilters, setWebFilters] = useState<WebFilters>(
    () => restoredState?.webFilters ?? EMPTY_WEB_FILTERS
  );
  const [currentPage, setCurrentPage] = useState(() => restoredState?.currentPage ?? 1);
  // Initialise from Redux so My Tickets doesn't show 0 while waiting for its own fetch.
  const [totalElements, setTotalElements] = useState(() => reduxTotalElements);
  const [filterTickets, setFilterTickets] = useState<MyTicketRow[]>([]);
  const [closedCount, setClosedCount] = useState(0);

  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Unfiltered queue for accurate status counts (same as Sustain Queue) ───
  const [unfilteredQueue, setUnfilteredQueue] = useState<MyTicketRow[]>([]);

  // ── Keep track of all tickets data for COUNT CALCULATIONS ONLY ───
  // This is never replaced when activeStatus changes to "resolved"
  // It ensures status pill counts remain constant regardless of active pill
  const allTicketsForCountsRef = useRef<MyTicketRow[]>([]);

  // ── Keep track of totalElements for COUNT CALCULATIONS ONLY ───
  // This is never replaced when activeStatus changes to "resolved"
  // It ensures the "All" count remains constant regardless of which pill is selected
  const totalElementsForCountsRef = useRef<number>(reduxTotalElements);

  // ── Flag to prevent unfiltered queue effect from overwriting refs after Closed restoration ───
  const justRestoredFromClosedRef = useRef<boolean>(false);

  // ── Closed-pill snapshot management (same as Sustain Queue) ────────────
  const prevActiveStatusRef = useRef<"all" | TicketStatus>(restoredState?.activeStatus ?? "all");
  const preClosedSnapshotRef = useRef<{
    tickets: MyTicketRow[];
    totalElements: number;
    page: number;
  } | null>(null);
  const preClosedCountDataRef = useRef<{
    allTicketsForCounting: MyTicketRow[];
    totalElements: number;
  } | null>(null);

  /**
   * Always-fresh ref — updated every render before any effect runs.
   * This prevents stale-closure bugs in effects that don't list every dep.
   */
  const latestRef = useRef({ query, activeStatus, sortColumn, sortDirection, webFilters, categoryConfig, currentPage, userEmail });
  // Update every render (no deps = runs before every paint)
  latestRef.current = { query, activeStatus, sortColumn, sortDirection, webFilters, categoryConfig, currentPage, userEmail };

  /** Build params from the freshest state and dispatch the tickets API call. */
  const doFetch = useCallback(async (overridePage?: number) => {
    const { query: q, activeStatus: as, sortColumn: sc, sortDirection: sd, webFilters: wf, categoryConfig: cc, currentPage: cp, userEmail: ue } = latestRef.current;

    const params: FetchMyTicketsParams = {
      sortBy: SORT_COLUMN_TO_API[sc] ?? "createdAt",
      sortOrder: sd.toUpperCase() as "ASC" | "DESC",
      pageNumber: overridePage ?? cp,
      pageSize: 100,
    };

    if (q.trim()) params.search = q.trim();

    // Only send status from webFilters (filter bar), NOT from activeStatus pill
    // This matches Sustain Queue behavior - activeStatus filters client-side
    if (wf.status.length) {
      params.status = STATUS_TO_API[wf.status[0]] ?? wf.status[0];
    }

    if (wf.urgency.length) {
      // Empty-urgency option maps to no entry in URGENCY_TO_API — send the literal
      // string "null" so the backend receives an explicit urgency=null query param
      // (axios drops real null/undefined values from the query string entirely).
      params.urgency = URGENCY_TO_API[wf.urgency[0]] || "null";
    }

    if (wf.category.length && cc.length) {
      const cat = cc.find((c) => c.name === wf.category[0]);
      if (cat) {
        params.categoryId = cat.id;
        if (wf.subcategory.length) {
          const sub = cat.subCategories?.find((s) => s.name === wf.subcategory[0]);
          if (sub) params.subCategoryId = sub.id;
        }
      }
    }

    if (ue) params.submitterEmail = ue;

    const result = await (dispatch(fetchMyTickets(params)) as unknown as Promise<{ tickets: MyTicketRow[]; totalElements: number }>);
    if (result?.totalElements !== undefined) {
      setTotalElements(result.totalElements);
      // When user clicks the Closed pill, update the closed count from the API response
      if (latestRef.current.activeStatus === "resolved") {
        setClosedCount(result.totalElements);
      } else {
        // Reset closed count when switching to any other status pill
        setClosedCount(0);
      }
    }
  }, [dispatch]); // dispatch is stable; all other values read from latestRef

  /** Fetch a larger set (pageSize=200) for populating filter dropdowns without affecting table state. */
  const doFetchFilters = useCallback(async () => {
    const result = await (dispatch(fetchMyTickets({
      pageSize: 200,
      pageNumber: 1,
      sortBy: "createdAt",
      sortOrder: "DESC",
      forFiltersOnly: true,
      submitterEmail: latestRef.current.userEmail || undefined,
    })) as unknown as Promise<{ tickets: MyTicketRow[]; totalElements: number }>);
    if (result?.tickets?.length) {
      setFilterTickets(result.tickets);
    }
  }, [dispatch]);

  // ── Initial fetch (mount only) ─────────────────────────────
  // When returning from Ticket Detail, restore the saved page/filters.
  // Clear the Redux snapshot so it is only consumed once.
  useEffect(() => {
    if (restoredState) {
      dispatch(saveMyTicketsListState(null)); // consume the snapshot
      // Force a fetch with the restored state (latestRef already reflects it)
      doFetch(restoredState.currentPage);
    } else if (myTickets.length === 0) {
      doFetch();
    }
    // Always fetch filters to get accurate status counts from unfiltered data
    doFetchFilters();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Re-fetch on filter/sort changes (skip mount) ────
  // Resets to page 1 and fetches directly to prevent duplicate calls from page change effect.
  // activeStatus is NOT included because it filters client-side only (like Sustain Queue)
  const isFilterMount = useRef(true);
  const skipPageEffectRef = useRef(false);
  useEffect(() => {
    if (isFilterMount.current) {
      isFilterMount.current = false;
      return;
    }
    skipPageEffectRef.current = true;  // Tell page effect to skip this trigger
    setCurrentPage(1);
    doFetch(1);  // Call with explicit page 1
    doFetchFilters();  // Refresh filter data for accurate counts
  }, [sortColumn, sortDirection, webFilters, doFetch, doFetchFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Re-fetch when page changes from pagination (skip first render & filter-triggered changes) ───
  const isPageMount = useRef(true);
  useEffect(() => {
    if (isPageMount.current) { 
      isPageMount.current = false; 
      return; 
    }
    if (skipPageEffectRef.current) {
      skipPageEffectRef.current = false;
      return;
    }
    doFetch();
  }, [currentPage, doFetch]);

  // ── Re-fetch on query change, debounced 350ms (skip mount) ─
  const isQueryMount = useRef(true);
  useEffect(() => {
    if (isQueryMount.current) {
      isQueryMount.current = false;
      return;
    }
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => { 
      skipPageEffectRef.current = true;  // Tell page effect to skip this trigger
      setCurrentPage(1); 
      doFetch(1);
      doFetchFilters();  // Refresh filter data for accurate counts 
    }, 350);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [query, doFetch, doFetchFilters]);
  // ── Status pill special-case: Closed should explicitly fetch from API ───
  // All other status pills filter client-side only (same as Sustain Queue)
  useEffect(() => {
    const prevStatus = prevActiveStatusRef.current;

    // Entering Closed: save snapshot and fetch closed tickets from API
    if (activeStatus === "resolved" && prevStatus !== "resolved") {
      // Save count data so we can restore it when leaving Closed
      preClosedCountDataRef.current = {
        allTicketsForCounting: allTicketsForCountsRef.current,
        totalElements: totalElementsForCountsRef.current,
      };

      preClosedSnapshotRef.current = {
        tickets: myTickets,
        totalElements: totalElements,
        page: currentPage,
      };

      skipPageEffectRef.current = true;
      setCurrentPage(1);
      
      // Fetch closed tickets explicitly
      const params: FetchMyTicketsParams = {
        sortBy: SORT_COLUMN_TO_API[sortColumn] ?? "createdAt",
        sortOrder: sortDirection.toUpperCase() as "ASC" | "DESC",
        pageNumber: 1,
        pageSize: 100,
        status: STATUS_TO_API["resolved"], // Force Closed status
      };
      
      if (query.trim()) params.search = query.trim();
      if (webFilters.urgency.length) params.urgency = URGENCY_TO_API[webFilters.urgency[0]] ?? webFilters.urgency[0];
      if (webFilters.category.length && categoryConfig.length) {
        const cat = categoryConfig.find((c) => c.name === webFilters.category[0]);
        if (cat) {
          params.categoryId = cat.id;
          if (webFilters.subcategory.length) {
            const sub = cat.subCategories?.find((s) => s.name === webFilters.subcategory[0]);
            if (sub) params.subCategoryId = sub.id;
          }
        }
      }
      if (userEmail) params.submitterEmail = userEmail;

      dispatch(fetchMyTickets(params)).then((result: any) => {
        if (result?.totalElements !== undefined) {
          setTotalElements(result.totalElements);
        }
      });
    }

    // Leaving Closed: restore snapshot or re-fetch
    if (activeStatus !== "resolved" && prevStatus === "resolved") {
      if (activeStatus === "all") {
        // Closed -> All: re-fetch default list (no status filter)
        skipPageEffectRef.current = true;
        setCurrentPage(1);
        doFetch(1);
        doFetchFilters();
      } else {
        // Closed -> other specific status: restore the snapshot (no API call)
        const snapshot = preClosedSnapshotRef.current;
        if (snapshot) {
          dispatch(setMyTickets(snapshot.tickets));
          dispatch(setMyTicketsTotalElements(snapshot.totalElements));
          setTotalElements(snapshot.totalElements);
          setCurrentPage(snapshot.page);
        }
        
        // Mark that we just restored so unfiltered queue effect doesn't overwrite refs
        justRestoredFromClosedRef.current = true;
      }
      preClosedSnapshotRef.current = null;
      
      // Restore the count data
      const countData = preClosedCountDataRef.current;
      if (countData) {
        allTicketsForCountsRef.current = countData.allTicketsForCounting;
        totalElementsForCountsRef.current = countData.totalElements;
      }
      preClosedCountDataRef.current = null;
    }

    prevActiveStatusRef.current = activeStatus;
  }, [activeStatus, myTickets, totalElements, currentPage, query, sortColumn, sortDirection, webFilters, categoryConfig, userEmail, dispatch, doFetch, doFetchFilters]);

  // ── Update unfiltered queue when filters are not active ───────────────────
  // Store the initial unfiltered data for status count calculations
  const hasActiveFiltersCheck = useMemo(() => 
    query.trim() ||
    webFilters.urgency.length > 0 ||
    webFilters.status.length > 0 ||
    webFilters.category.length > 0 ||
    webFilters.subcategory.length > 0 ||
    webFilters.location.length > 0 ||
    webFilters.title.length > 0
  , [query, webFilters]);

  useEffect(() => {
    // Skip updating refs if we just restored from Closed status
    // The refs were already restored with correct count data in the activeStatus effect
    if (justRestoredFromClosedRef.current) {
      justRestoredFromClosedRef.current = false;
      return;
    }
    
    // Only update unfiltered queue when no filters are active AND not in closed status
    // When activeStatus is "resolved", myTickets contains only closed tickets (don't use for counts)
    if (!hasActiveFiltersCheck && activeStatus !== "resolved" && myTickets.length > 0) {
      setUnfilteredQueue(myTickets);
      allTicketsForCountsRef.current = myTickets;
      // Also update totalElementsForCounts when we have fresh unfiltered data
      totalElementsForCountsRef.current = totalElements;
    }
  }, [myTickets, hasActiveFiltersCheck, activeStatus, totalElements]);
  /**
   * Fire an in-app (+ simulated email/Teams) notification whenever a user-scoped
   * ticket transitions to "Waiting on User" and hasn’t already been notified.
   * Uses `notifiedWaitingTicketIds` in Redux to prevent duplicate notifications.
   */
  useEffect(() => {
    myTickets
      .filter(
        (t) =>
          t.status === "waiting-user" &&
          !notifiedWaitingTicketIds.includes(t.id) &&
          (!userEmail || (
            t.submittedBy === userEmail ||
            t.stakeholders?.toLowerCase().includes(userEmail.toLowerCase()) ||
            t.mergedUserEmails?.includes(userEmail)
          ))
      )
      .forEach((t) => {
        const notif: UserTicketNotif = {
          id: `waitinguser-${t.id}-${Date.now()}`,
          ticketId: t.id,
          message: `The Sustain Team has commented on Ticket ${t.id} and is waiting for your response.`,
          createdAt: new Date().toISOString(),
          read: false,
          channels: ["in-app", "email", "teams"],
        };
        dispatch(addUserTicketNotif(notif));
      });
  }, [myTickets, notifiedWaitingTicketIds, dispatch]);

  /**
   * The backend already returns only the tickets the current user should see
   * (tickets where the user is submitter OR stakeholder). The submitterEmail
   * parameter sent in the API request tells the backend to include both roles.
   *
   * DO NOT apply any additional client-side filtering here. A previous filter
   * of `t.submittedBy === userEmail` was incorrectly removing stakeholder tickets
   * (where submitterEmail in the response belongs to another user but the logged-in
   * user is a stakeholder), causing the table to show fewer rows than the API count.
   */
  const userScopedTickets = useMemo(() => myTickets, [myTickets]);

  /** Check if any filters or search are currently active (excluding activeStatus) */
  const hasActiveFilters = useMemo(() =>
    query.trim() ||
    webFilters.urgency.length > 0 ||
    webFilters.status.length > 0 ||
    webFilters.category.length > 0 ||
    webFilters.subcategory.length > 0 ||
    webFilters.location.length > 0 ||
    webFilters.title.length > 0
  , [query, webFilters]);

  const statusCounts = useMemo(() => {
    // ALWAYS use allTicketsForCounts for counts (never use myTickets which changes with activeStatus)
    // This ensures status pill counts remain consistent regardless of which pill is selected
    const dataForCounts = allTicketsForCountsRef.current.length > 0 
      ? allTicketsForCountsRef.current 
      : unfilteredQueue.length > 0
        ? unfilteredQueue
        : userScopedTickets;
    
    // Use totalElementsForCountsRef for "all" count to show accurate total (never changes with activeStatus)
    const counts: Record<string, number> = { all: totalElementsForCountsRef.current };
    STATUS_PILLS.forEach((pill) => {
      if (pill.key !== "all") {
        // Use reduxClosedCount from API for "resolved" (Closed) status
        if (pill.key === "resolved") {
          counts[pill.key] = reduxClosedCount;
        } else {
          counts[pill.key] = dataForCounts.filter((t) => t.status === pill.key).length;
          counts[pill.key] = dataForCounts.filter((t) => t.status === pill.key).length;
        }
      }
    });
    return counts;
  }, [unfilteredQueue, userScopedTickets, reduxClosedCount]);

  /** Filter options are derived from the full filter-data set (pageSize=100 fetch),
   *  falling back to userScopedTickets if the filter fetch hasn't completed yet.
   *  Special values "National" and "COE" are always included in locations filter. */
  const filterOptions = useMemo<FilterOptions>(() => {
    const source = filterTickets.length > 0 ? filterTickets : userScopedTickets;
    // Exclude tickets with no/unknown urgency so the dropdown never shows a blank option
    const urgencies = [...new Set(source.map((t) => t.urgency).filter(Boolean))].sort(
      (a, b) => (URGENCY_SORT_ORDER[a] ?? 99) - (URGENCY_SORT_ORDER[b] ?? 99)
    );
    const statuses = [...new Set(source.map((t) => t.status))].sort(
      (a, b) => (STATUS_SORT_ORDER[a] ?? 99) - (STATUS_SORT_ORDER[b] ?? 99)
    );
    const categories = [...new Set(source.map((t) => t.category))].sort();
    const subcategories = [
      ...new Set(source.map((t) => t.subcategory).filter(Boolean) as string[]),
    ].sort();
    const baseLocations = [...new Set(source.map((t) => t.location))].sort();
    const locations = ["COE", "National", ...baseLocations];
    const titles = [...new Set(source.map((t) => t.title))].sort();
    return { urgencies, statuses, categories, subcategories, locations, titles };
  }, [filterTickets, userScopedTickets]);

  const filteredTickets = useMemo(() => {
    // Apply client-side filters: activeStatus, location, title, subcategory
    // API handles: search, urgency, category (from webFilters only)
    let result = userScopedTickets.filter((ticket) => {
      // Filter by activeStatus pill selection (same as Sustain Queue)
      if (activeStatus !== "all" && ticket.status !== activeStatus) return false;
      
      // When location filters are active, use strict matching
      // When no location filters selected, show all (including National and COE)
      if (webFilters.location.length > 0 && !webFilters.location.includes(ticket.location)) return false;
      if (webFilters.title.length && !webFilters.title.includes(ticket.title)) return false;
      if (
        webFilters.subcategory.length &&
        !webFilters.subcategory.includes(ticket.subcategory ?? "")
      )
        return false;
      return true;
    });
    return sortTickets(result, sortColumn, sortDirection);
  }, [userScopedTickets, activeStatus, webFilters, sortColumn, sortDirection]);

  function handleSort(column: SortColumn) {
    if (column === sortColumn) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  }

  const totalPages = Math.max(1, Math.ceil(totalElements / 100));

  function handlePageChange(page: number) {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  }

  const hasActiveWebFilters = Object.values(webFilters).some((v) => v.length > 0);

  return {
    query,
    activeStatus,
    statusPills: STATUS_PILLS,
    statusCounts,
    filteredTickets,
    totalCount: totalElements || userScopedTickets.length,
    sortColumn,
    sortDirection,
    webFilters,
    filterOptions,
    hasActiveWebFilters,
    loading: myTicketsLoading,
    error: myTicketsError,
    currentPage,
    totalPages,
    setQuery,
    setActiveStatus,
    handleSort,
    setWebFilters,
    handlePageChange,
  };
};

export default useMyTicketsListing;

